// SPDX-License-Identifier: AGPL-3.0-or-later

import {makeAutoObservable, runInAction} from 'mobx';

export type NativeParticipantE2EEState = 'encrypted' | 'decryption_failed' | 'unencrypted' | 'unknown';

export function normalizeParticipantE2EEState(raw: unknown): NativeParticipantE2EEState {
	switch (raw) {
		case 'encrypted':
		case 'ok':
			return 'encrypted';
		case 'decryption_failed':
		case 'decryptionFailed':
		case 'error':
		case 'broken':
			return 'decryption_failed';
		case 'unencrypted':
		case 'missing_key':
		case 'missingKey':
		case 'internal_error':
			return 'unencrypted';
		default:
			return 'unknown';
	}
}

class NativeVoiceE2EEStore {
	private _stateBySid: Readonly<Record<string, NativeParticipantE2EEState>> = {};

	constructor() {
		makeAutoObservable(this, {getStateForSid: false, hasAnyState: false}, {autoBind: true});
	}

	get stateBySid(): Readonly<Record<string, NativeParticipantE2EEState>> {
		return this._stateBySid;
	}

	hasAnyState(): boolean {
		for (const _ in this._stateBySid) return true;
		return false;
	}

	getStateForSid(sid: string): NativeParticipantE2EEState | undefined {
		return this._stateBySid[sid];
	}

	setState(sid: string, raw: unknown): void {
		if (!sid) return;
		const next = normalizeParticipantE2EEState(raw);
		if (this._stateBySid[sid] === next) return;
		runInAction(() => {
			this._stateBySid = {...this._stateBySid, [sid]: next};
		});
	}

	remove(sid: string): void {
		if (!(sid in this._stateBySid)) return;
		runInAction(() => {
			const next = {...this._stateBySid};
			delete next[sid];
			this._stateBySid = next;
		});
	}

	clear(): void {
		if (!this.hasAnyState()) return;
		runInAction(() => {
			this._stateBySid = {};
		});
	}

	aggregateStatus(): 'encrypted' | 'broken' | 'none' {
		let sawAny = false;
		for (const state of Object.values(this._stateBySid)) {
			sawAny = true;
			if (state !== 'encrypted') return 'broken';
		}
		return sawAny ? 'encrypted' : 'none';
	}
}

const instance = new NativeVoiceE2EEStore();

export default instance;
export {NativeVoiceE2EEStore};
